import React, { FC, useEffect, useState } from 'react'
import { defineMessages, FormattedMessage } from 'react-intl'
import { getOrderForm } from '@/checkout-instore-legacy/utils/orderFormUtils'
import {
  isTakeAwaySlaType,
  getSlaTypeInfoForLogisticsInfoItem,
} from '@/checkout-instore-legacy/shipping/utils'
import { getItemPayload } from '@/checkout-instore-legacy/utils/ProductUtils'
import { useProductContext } from '../context'
import InlineMenu from '@/checkout-instore-legacy/components/Commons/Menu/InlineMenu'
import { ClickableArea } from '../ProductSummary'
import { ItemPayload } from '@/checkout-instore-legacy/cart/message/types'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'
import TrashIcon from '@/checkout-instore-legacy/components/Commons/Icons/Trash'
import { getSkuSellersSwitchInfo } from '@/checkout-instore-legacy/cart/utils'
import {
  getDefaultSellerId,
  usingMultiSeller,
} from '@/checkout-instore-legacy/seller/utils'

const intlMessage = defineMessages({
  removeItem: {
    id: 'cartRemoveFromCart',
    defaultMessage: '',
  },
  toDelivery: {
    id: 'cartChangeToDelivery',
    defaultMessage: '',
  },
  toTakeAway: {
    id: 'cartChangeToTakeAway',
    defaultMessage: '',
  },
  switchSeller3p: {
    id: 'switchSeller3p',
    defaultMessage: '',
  },
  switchSeller1p: {
    id: 'switchSeller1p',
    defaultMessage: '',
  },
})

export interface ProductOptionsProps {
  removeItem: (item: ItemPayload) => void
  switchSeller: (item: Item) => void
  changeSla: (item: Item) => void
  enabled: {
    switchSeller: boolean
    removeItem: boolean
    changeSla: boolean
  }
}

const ProductOptions: FC<ProductOptionsProps> = ({
  removeItem,
  changeSla,
  switchSeller,
  enabled,
}) => {
  const { switchSeller: switchSellerConfig } = getInstoreConfig()
  const [canSwitchSeller, setCanSwitchSeller] = useState<Optional<boolean>>(
    null
  )
  const [defaultSellerName, setDefaultSellerName] = useState<Optional<string>>(
    null
  )
  const { item } = useProductContext()
  const { canChangeSlaType, slaType } = getSlaTypeInfoForLogisticsInfoItem(
    getOrderForm(),
    item.index
  )
  const handleRemove = () => {
    const itemPayload = getItemPayload(item)
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'Pick<Item, "ean" | "id" | "index... Remove this comment to see the full error message
    removeItem(itemPayload)
  }

  useEffect(() => {
    const updateSellerInfo = async () => {
      if (usingMultiSeller()) {
        const {
          singleSeller,
          sellerDefaultName,
        } = await getSkuSellersSwitchInfo(item)
        const isDelivery = !isTakeAwaySlaType(slaType)
        setCanSwitchSeller(!singleSeller && isDelivery)
        setDefaultSellerName(sellerDefaultName)
      } else {
        setCanSwitchSeller(false)
      }
    }

    if (item.index != null && switchSellerConfig.enabled) {
      updateSellerInfo()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.index, slaType, switchSellerConfig.enabled])

  const handleSwitchSeller = () => {
    switchSeller(item)
  }

  const handleChangeSLA = () => {
    changeSla(item)
  }

  const switchSellerMessage =
    item.seller === getDefaultSellerId()
      ? intlMessage.switchSeller3p
      : intlMessage.switchSeller1p

  const OPTION = {
    removeItem: {
      label: <FormattedMessage {...intlMessage.removeItem} />,
      isDangerous: true,
      onClick: handleRemove,
      enabled: enabled.removeItem,
    },
    changeToDelivery: {
      label: <FormattedMessage {...intlMessage.toDelivery} />,
      onClick: handleChangeSLA,
      enabled: enabled.changeSla,
    },
    changeToTakeaway: {
      label: <FormattedMessage {...intlMessage.toTakeAway} />,
      onClick: handleChangeSLA,
      enabled: enabled.changeSla,
    },
    switchSeller: {
      label: (
        <FormattedMessage
          {...switchSellerMessage}
          values={{ seller: defaultSellerName }}
        />
      ),
      onClick: handleSwitchSeller,
      enabled: enabled.switchSeller,
    },
  }

  const getOptions = () => {
    const {
      removeItem,
      changeToDelivery,
      changeToTakeaway,
      switchSeller,
    } = OPTION
    const isTakeAway = isTakeAwaySlaType(slaType)
    const changeSla = isTakeAway ? changeToDelivery : changeToTakeaway

    return [
      removeItem,
      ...(canChangeSlaType ? [changeSla] : []),
      ...(canSwitchSeller ? [switchSeller] : []),
    ]
  }

  const options = getOptions()

  const content =
    options.length === 1 ? (
      <div
        onClick={handleRemove}
        data-cy="product-delete-icon-button"
        className="h2 w2 pv1 flex items-center justify-center c-on-action-primary"
      >
        <TrashIcon className="c-action-primary hover-c-action-primary c-default" />
      </div>
    ) : (
      <InlineMenu options={options} />
    )

  return (
    <div className="nt2-s nt0-ns">
      <ClickableArea>{content}</ClickableArea>
    </div>
  )
}

export default ProductOptions
